import type {
  AdType,
  CreateProductSetForBudgetsPurchaseForHirerMutation,
  CreateProductSetForBudgetsPurchaseForVisitorMutation,
  DisplayRecommendation,
  TaxCode,
} from '../../generated/graphql-types';
import { isAnz } from '../../utils/isAnz';

interface AdRange {
  minimum: number;
  maximum: number;
}

export type BudgetTypes = 'FlexibleBudget' | 'BrandedBudget' | 'PremiumBudget';

export interface BudgetOption {
  adPostingFrequency: 'Occasional' | 'Regular' | 'Frequent';
  adRange: AdRange;
  budgetId: string;
  budgetName: string;
  budgetType: BudgetTypes;
  budgetValue: number;
  currency: string;
  discounts?: Array<FixedAmountDiscount | PercentageDiscount>;
  duration: {
    value: number;
    unit: 'month';
  };
  productId: string;
  purchasableProducts: string[] | string;
  tax?: {
    code: TaxCode;
    displayRecommendation: DisplayRecommendation;
    value: number;
  };
  group?: {
    displayName: string;
    key: BudgetTypes;
  };
}

interface BaseDiscount {
  type: 'FixedAmount' | 'Percentage';
  adType: 'Classic' | 'Basic' | 'StandOut' | 'Branded' | 'Premium';
}

type FixedAmountDiscount = BaseDiscount & {
  type: 'FixedAmount';
  value: number;
};

type PercentageDiscount = BaseDiscount & {
  type: 'Percentage';
  value: number;
};

type CreateProductSetForBudgetsPurchase =
  | CreateProductSetForBudgetsPurchaseForVisitorMutation['createProductSetForBudgetsPurchaseForVisitor']
  | CreateProductSetForBudgetsPurchaseForHirerMutation['createProductSetForBudgetsPurchaseForHirer'];

type AdBudgetItem =
  CreateProductSetForBudgetsPurchase['productSet']['items'][number];

type AdBudgetProductItem = Extract<
  AdBudgetItem,
  { __typename: 'AdBudgetProduct' }
>;

type AdBudgetProductItemPrice = Extract<
  AdBudgetProductItem['price'],
  { __typename: 'Price' }
>;
export type AdBudgetProductItemType = Omit<AdBudgetProductItem, 'price'> & {
  price: AdBudgetProductItemPrice;
};
// This determines if it is a Branded budget
// TODO https://myseek.atlassian.net/browse/AT-7221
// We want to remove this function in the future, since moving forward,
// we would like to depend on the key in group in the budgetOption types in order to differentiate between different budget types.
// However, since for the time being, that group is optional and not mandatory YET, we still need this.
export const isBrandedFeaturesFeaturePresent = (
  adBudgetProduct: AdBudgetProductItemType,
) =>
  Boolean(
    adBudgetProduct.features.find(
      (feature) => feature?.__typename === 'BrandedFeaturesFeature',
    ),
  );

const getDisplayAdType = (
  adType: AdType,
  country: string,
): 'Classic' | 'Basic' | 'StandOut' | 'Branded' | 'Premium' => {
  if (adType === 'CLASSIC' && isAnz(country)) {
    return 'Classic';
  }
  if (adType === 'CLASSIC') {
    return 'Basic';
  }
  if (
    (adType === 'BRANDED_UPGRADE' || adType === 'BRANDED') &&
    isAnz(country)
  ) {
    return 'StandOut';
  }
  if (adType === 'BRANDED_UPGRADE' || adType === 'BRANDED') {
    return 'Branded';
  }
  if (adType === 'PREMIUM') {
    return 'Premium';
  }
  throw new Error('Invalid adType');
};

// TODO move this to backend
const mapBudgetOption = (
  adBudgetProduct: AdBudgetProductItemType,
  country: string,
): BudgetOption => {
  const {
    adPostingFrequency,
    adRange,
    purchasableProducts,
    id: productId,
  } = adBudgetProduct;

  if (
    adPostingFrequency !== 'Occasional' &&
    adPostingFrequency !== 'Regular' &&
    adPostingFrequency !== 'Frequent'
  ) {
    throw new Error();
  }

  const durationFeature = adBudgetProduct.features.flatMap((feature) =>
    feature?.__typename === 'DurationFeature' ? [feature] : [],
  )[0];

  if (durationFeature === undefined) {
    throw new Error('no duration feature');
  }

  const discounts = adBudgetProduct.features
    .flatMap((feature) =>
      feature?.__typename === 'AdTypeDiscountsFeature'
        ? [
            feature.discounts.flatMap(
              (
                discountFeature,
              ): Array<FixedAmountDiscount | PercentageDiscount> => {
                if (
                  discountFeature.discount.discountPercentage > 0 &&
                  discountFeature.discount.discountAmount.value > 0
                ) {
                  throw new Error(
                    'fixed and percentage discounts specified in single feature',
                  );
                }
                if (discountFeature.discount.discountPercentage > 0) {
                  return [
                    {
                      type: 'Percentage',
                      adType: getDisplayAdType(discountFeature.adType, country),
                      value: discountFeature.discount.discountPercentage,
                    },
                  ];
                }
                if (discountFeature.discount.discountAmount.value > 0) {
                  return [
                    {
                      type: 'FixedAmount',
                      adType: getDisplayAdType(discountFeature.adType, country),
                      value: discountFeature.discount.discountAmount.value,
                    },
                  ];
                }
                return [];
              },
            ),
          ]
        : [],
    )
    .flat();

  const hasFixedAmountDiscounts = discounts.some(
    (discount) => discount.type === 'FixedAmount',
  );

  let discountsToDisplay = discounts;

  /* This removes Classic/Basic ad discount for Branded budgets if branded ad component is fixed compared to classic component
   * Example for AU/NZ (has Classic ads discount)
   * 15% off Classic Ads
   * $5 off StandOut features
   *
   * Example for TH (No Basic ads discount)
   * 37% off Basic ads <-- this is removed
   * 37% off Branded ads
   * 10% off Premium ads
   */

  // TODO https://myseek.atlassian.net/browse/AT-7221
  // We want to remove the isBrandedFeaturesFeaturePresent function in the future, since moving forward,
  // we would like to depend on the key in group in the budgetOption types in order to differentiate between different budget types.
  // However, since for the time being, that group is optional and not mandatory YET, we still need this.
  if (!hasFixedAmountDiscounts)
    if (adBudgetProduct.group?.key) {
      switch (adBudgetProduct.group?.key) {
        case 'PremiumBudget':
          discountsToDisplay = discounts.filter(
            (discount) => discount.adType === 'Premium',
          );
          break;
        case 'BrandedBudget':
          discountsToDisplay = discounts.filter(
            (discount) =>
              discount.adType !== 'Classic' && discount.adType !== 'Basic',
          );
      }
    } else if (isBrandedFeaturesFeaturePresent(adBudgetProduct)) {
      discountsToDisplay = discounts.filter(
        (discount) =>
          discount.adType !== 'Classic' && discount.adType !== 'Basic',
      );
    }

  const getBudgetType = () => {
    if (adBudgetProduct.group?.key) {
      return adBudgetProduct.group?.key;
    }

    if (isBrandedFeaturesFeaturePresent(adBudgetProduct)) {
      return 'BrandedBudget';
    }

    return 'FlexibleBudget';
  };

  if (!purchasableProducts) {
    throw new Error('no purchasable products');
  }

  let productToDisplay: string | string[] = '';
  if (
    purchasableProducts.__typename === 'PurchasableProductsList' &&
    purchasableProducts.products
  ) {
    productToDisplay = purchasableProducts.products;
  }
  if (
    purchasableProducts.__typename === 'PurchasableProductsString' &&
    purchasableProducts.product
  ) {
    productToDisplay = purchasableProducts.product;
  }

  const durationUnit = durationFeature.unit.toLowerCase();

  if (durationUnit !== 'month') {
    throw new Error(`unsupported duration unit: ${durationFeature.unit}`);
  }

  const code = adBudgetProduct.price.tax?.code ?? undefined;
  const displayRecommendation =
    adBudgetProduct.price.tax?.displayRecommendation ?? undefined;
  const value = adBudgetProduct.price.tax?.amount?.value ?? undefined;

  return {
    budgetValue: adBudgetProduct.price.netPrice.value,
    currency: adBudgetProduct.price.netPrice.currency,
    tax:
      code && displayRecommendation && value
        ? {
            code,
            value,
            displayRecommendation,
          }
        : undefined,
    adPostingFrequency,
    budgetId: adBudgetProduct.id,
    budgetName: adBudgetProduct.name,
    budgetType: (adBudgetProduct.group?.key as BudgetTypes) ?? getBudgetType(),
    discounts: discountsToDisplay,
    duration: {
      value: durationFeature.value,
      unit: durationUnit,
    },
    adRange,
    productId,
    purchasableProducts: productToDisplay,
  };
};

export { mapBudgetOption };
